import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  CircularProgress,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import { TYPE } from '@/theme/typography';
import { AvailableGateway, billingApi } from '@/api/endpoints/billing';

interface GatewaySelectorProps {
  countryCode?: string | null;
  value: string | null;
  onChange: (key: string) => void;
  forRecurring?: boolean;
}

/**
 * Country-appropriate payment gateway picker for the checkout modal.
 * Reads /billing/available-gateways and renders a single-select list,
 * defaulting to the backend-recommended gateway for the tenant's country.
 */
export default function GatewaySelector({
  countryCode,
  value,
  onChange,
  forRecurring,
}: GatewaySelectorProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['billing', 'available-gateways', countryCode ?? 'any'],
    queryFn: () =>
      billingApi.listAvailableGateways(countryCode ?? undefined).then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const gateways = useMemo<AvailableGateway[]>(() => {
    const all = data?.gateways ?? [];
    return forRecurring ? all.filter(g => g.supports_recurring !== false) : all;
  }, [data, forRecurring]);
  const defaultKey = data?.default ?? null;

  // Auto-select the recommended default once the list loads.
  if (!value && defaultKey && gateways.some((g) => g.key === defaultKey)) {
    queueMicrotask(() => onChange(defaultKey));
  }

  if (isLoading) {
    return (
      <Stack alignItems="center" py={2}>
        <CircularProgress size={20} />
      </Stack>
    );
  }

  if (isError) {
    return (
      <Alert severity="warning" sx={{ fontSize: TYPE.xs }}>
        Couldn't load payment options — you can still continue and we'll pick the
        best one for your country.
      </Alert>
    );
  }

  if (!gateways.length) {
    return (
      <Alert severity="info" sx={{ fontSize: TYPE.xs }}>
        No payment gateways are configured for this account yet. Contact support
        to enable checkout.
      </Alert>
    );
  }

  if (gateways.length === 1) {
    const g = gateways[0];
    return (
      <Box sx={{ mt: 1 }}>
        <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', mb: 0.5 }}>
          Payment processor
        </Typography>
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.semibold }}>
            {g.label}
          </Typography>
          <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>
            {g.description}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', mb: 1 }}>
        Choose how you'd like to pay
      </Typography>
      <Stack spacing={1}>
        {gateways.map((g) => {
          const selected = value === g.key;
          return (
            <Box
              key={g.key}
              role="button"
              tabIndex={0}
              aria-label={`Select ${g.label}`}
              aria-pressed={selected}
              onClick={() => onChange(g.key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onChange(g.key);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                p: 1.25,
                borderRadius: 1.5,
                border: '2px solid',
                borderColor: selected ? 'primary.main' : 'divider',
                bgcolor: 'background.paper',
                cursor: 'pointer',
                transition: 'border-color 0.15s ease',
                '&:hover': { borderColor: 'primary.main' },
              }}
            >
              <Radio checked={selected} size="small" sx={{ p: 0.5, mt: 0.25 }} />
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.semibold }}>
                    {g.label}
                  </Typography>
                  {g.is_default && (
                    <Typography
                      sx={{
                        fontSize: TYPE.xs,
                        fontWeight: TYPE.semibold,
                        color: 'primary.main',
                        bgcolor: 'primary.50',
                        px: 0.75,
                        py: 0.125,
                        borderRadius: 0.75,
                      }}
                    >
                      RECOMMENDED
                    </Typography>
                  )}
                </Stack>
                <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>
                  {g.description}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
